import { KeyboardKey } from '@constants/keyboardKey'
import { useKeyPress } from '@hooks/useKeyPress'
import { useStyles } from '@hooks/useStyles'
import { SurfaceLevel } from '@ui-kit/types/surface'
import { AnimatePresence, motion } from 'framer-motion'
import { FC, MouseEventHandler, useCallback, useMemo } from 'react'

import { ModalContext } from '../context'
import { ModalProps } from '../Modal'
import styles from '../Modal.module.scss'

export interface SideModalProps
  extends Omit<ModalProps, 'isCentered' | 'size' | 'level'> {
  surface?: SurfaceLevel
}

export const SideModal: FC<SideModalProps> = ({
  children,
  className = '',
  onClose,
  surface = 1,
  'data-testid': dataTestId = 'side-modal',
  ...restProps
}) => {
  const modalStyles = useStyles(
    {
      [styles.sideModal]: true,
      [styles[`sideModal--surface-${surface}`]]: true
    },
    className
  )

  const modalCtx = useMemo(
    () => ({ onClose, isCentered: false, isFullscreen: false }),
    [onClose]
  )

  const cancelPropagation: MouseEventHandler = useCallback(
    e => e.stopPropagation(),
    []
  )

  useKeyPress(KeyboardKey.Escape, onClose)

  return (
    <ModalContext.Provider value={modalCtx}>
      <AnimatePresence>
        <motion.div
          key='modal'
          initial='collapsed'
          animate='open'
          exit='collapsed'
          variants={{
            open: { right: '0px' },
            collapsed: { right: '-550px' }
          }}
          transition={{ duration: 0.5, ease: [0.05, 0, 0.3, 1] }}
          className={modalStyles}
          data-testid={`${dataTestId}-wrapper`}
        >
          <div
            data-testid={dataTestId}
            onClick={cancelPropagation}
            {...restProps}
          >
            {children}
          </div>
        </motion.div>
      </AnimatePresence>
    </ModalContext.Provider>
  )
}

SideModal.displayName = 'SideModal'
